import {Alert, Box, Button, Container, Group, Stack, Text, Title} from '@mantine/core'
import * as Sentry from '@sentry/react'
import type {ErrorComponentProps} from '@tanstack/react-router'
import {useRef, useState} from 'react'

import {isSentryEnabled} from '../sentry/is-sentry-enabled'

type FeedbackDialog = {
    appendToDom: () => void
    removeFromDom: () => void
    open: () => void
}

export function ClientErrorFallback({error}: ErrorComponentProps) {
    const [isOpeningFeedback, setIsOpeningFeedback] = useState(false)
    const feedbackFormRef = useRef<FeedbackDialog | null>(null)
    const sentryEnabled = isSentryEnabled()
    const errorMessage = error instanceof Error ? error.message : String(error)
    const errorStack = error instanceof Error ? error.stack : undefined

    const handleFeedbackClick = async () => {
        const feedback = Sentry.getFeedback()
        if (!feedback || isOpeningFeedback) {
            return
        }

        setIsOpeningFeedback(true)
        try {
            if (!feedbackFormRef.current) {
                feedbackFormRef.current = await feedback.createForm({
                    formTitle: 'Send Feedback',
                    onFormSubmitted: () => {
                        feedbackFormRef.current?.removeFromDom()
                        feedbackFormRef.current = null
                    },
                })
            }

            feedbackFormRef.current.appendToDom()
            feedbackFormRef.current.open()
        } finally {
            setIsOpeningFeedback(false)
        }
    }

    return (
        <Box mih="100%" p="xl">
            <Container size="sm">
                <Stack gap="md">
                    <Title order={1}>Error</Title>
                    <Text>Failed to load this part of Depot.</Text>

                    {sentryEnabled && (
                        <Alert color="green" title="This error has been reported">
                            The error was sent to the development team.
                        </Alert>
                    )}

                    {sentryEnabled && (
                        <Group>
                            <Text>
                                Please add any additional information about what you were trying to
                                do when the error occurred.
                            </Text>
                            <Button
                                type="button"
                                onClick={() => {
                                    void handleFeedbackClick()
                                }}
                                loading={isOpeningFeedback}
                            >
                                Send feedback
                            </Button>
                        </Group>
                    )}

                    <details>
                        <summary>Show technical details</summary>
                        <Stack gap="xs" mt="sm">
                            <Text fw={600}>Error message</Text>
                            <Box component="pre" m={0} style={{whiteSpace: 'pre-wrap'}}>
                                {errorMessage}
                            </Box>
                            {errorStack && (
                                <>
                                    <Text fw={600}>Stack</Text>
                                    <Box component="pre" m={0} style={{whiteSpace: 'pre-wrap'}}>
                                        {errorStack}
                                    </Box>
                                </>
                            )}
                        </Stack>
                    </details>
                </Stack>
            </Container>
        </Box>
    )
}
